import React from 'react';
import PropTypes from 'prop-types';
import {
  Comment,
  Icon,
  Tooltip,
} from 'antd';
import moment from 'moment';
import _ from 'lodash';

import defaultUserAvatarImage from '../../../../../styles/images/no_testimonial_avatar_image.png';
import i18n from '../../../../../config/i18n';
import './styles/UserMessageItem.css';

const MAX_SEEN_BY_COUNT = 3;

// Note: We do not show the current user name since it is redundant.
const renderSeenInformation = ({
  currentUserId,
  message: {
    seq,
  },
  membersByUserId,
}) => {
  const members = Object.values(membersByUserId);
  const seenByMembers = members.filter(
    member => member.currentMsgIdx === seq
      && member.userId !== currentUserId // Exclude the current user.
      && member.lastViewedAt
  );
  let text;

  if (seenByMembers.length <= 0) {
    return null;
  } else if (
    seenByMembers.length >= members.length - 1

    // Only the last message will show 'Seen by all'.
    && membersByUserId[currentUserId].currentMsgIdx === seq
  ) {
    text = i18n.t(['seenBy', 'all']);
  } else if (seenByMembers.length <= MAX_SEEN_BY_COUNT) {
    text = i18n.t(['seenBy', 'less'], { names: seenByMembers.map(member => member.user.firstName).join(', ') });
  } else if (seenByMembers.length > MAX_SEEN_BY_COUNT) {
    const showedNames = [];
    const hiddenNames = [];

    seenByMembers.forEach(({ user: { firstName } }) => (
      showedNames.length < MAX_SEEN_BY_COUNT - 1
        ? showedNames.push(firstName)
        : hiddenNames.push(firstName)
    ));

    text = (
      <Tooltip
        placement="topRight"
        title={
          <span className="message-item-seen-info-text">
            {hiddenNames.map((name, index) => (
              <React.Fragment key={index.toString()}>
                {name}
                <br />
              </React.Fragment>
            ))}
          </span>
        }
      >
        {i18n.t(['seenBy', 'more'], { names: showedNames.join(', '), otherCount: hiddenNames.length })}
      </Tooltip>
    );
  }

  return [
    <div className="message-item-seen-info">
      <Icon className="message-item-seen-info-icon" key="icon" type="check" />
      <span className="message-item-seen-info-text" key="text">{text}</span>
    </div>,
  ];
};

const renderAvatar = ({
  message: {
    avatar,
    createdAt,
    simple,
  },
}) => (
  simple
    ? <div className="message-item-empty-avatar">{moment(createdAt).format('hh:mm')}</div>
    : (avatar || defaultUserAvatarImage)
);

const parseTags = (text, parser, parserIdx) => (
  text.split(parser.pattern).map((subStr, index) => (
    index % 2 === 1 ? parser.render(text, `${parserIdx} ${index}`) : subStr
  ))
);

const applyTagParsers = (text, tagParsers) => (
  tagParsers.reduce((result, parser, parserIdx) => {
    const tmpResult = [];

    result.forEach((subStr, index) => {
      if (index % 2 === 0) {
        tmpResult.push(...parseTags(subStr, parser, parserIdx));
      } else {
        tmpResult.push(subStr);
      }
    });

    return tmpResult;
  }, [text])
);

const renderContent = ({
  message: {
    photoUrl,
    text,
    type,
  },
  measure,
  tagParsers,
}) => {
  switch (type) {
    case 'text': {
      return (
        <div
          className="message-item-content-detail-inner"
        >
          {applyTagParsers(text, tagParsers)}
        </div>
      );
    }
    case 'image': {
      return (
        <img
          className="message-item-content-detail-inner image"
          src={photoUrl}
          alt={i18n.t('uploadPhoto')}
          onLoad={measure}
        />
      );
    }
    case 'call': {
      return (
        <div className="message-item-content-detail-inner call">
          <Icon
            className="message-item-content-detail-inner-call-icon"
            theme="filled"
            type="phone"
          />
          <div>{text}</div>
        </div>
      );
    }
    default: {
      return null;
    }
  }
};

const UserMessageItem = (props) => {
  const {
    currentUserId,
    membersByUserId,
    message: {
      createdAt,
      creatorId,
      simple,
    },
    style,
  } = props;
  const { firstName, lastName } = _.get(membersByUserId, [creatorId, 'user'], {});

  return (
    <Comment
      actions={renderSeenInformation(props)}
      author={!simple && `${firstName} ${lastName}`}
      avatar={renderAvatar(props)}
      className={`${currentUserId === creatorId ? 'reverse' : ''} ${simple ? 'simple' : ''}`}
      content={renderContent(props)}
      datetime={!simple && moment(createdAt).format('hh:mm a')}
      prefixCls="message-item"
      style={style}
    />
  );
};

UserMessageItem.propTypes = {
  currentUserId: PropTypes.number,
  measure: PropTypes.func,
  membersByUserId: PropTypes.object,
  message: PropTypes.shape({
    createdAt: PropTypes.string,
    creatorId: PropTypes.number,
    id: PropTypes.number,
    seq: PropTypes.number,
    simple: PropTypes.bool,
    text: PropTypes.string,
    type: PropTypes.string,
  }),
  style: PropTypes.object,
  tagParsers: PropTypes.arrayOf(PropTypes.shape({
    pattern: PropTypes.instanceOf(RegExp).isRequired,
    render: PropTypes.func.isRequired,
  })),
};

UserMessageItem.defaultProps = {
  currentUserId: 0,
  measure: () => {},
  membersByUserId: {},
  message: {},
  style: {},
  tagParsers: [],
};

export default UserMessageItem;



// WEBPACK FOOTER //
// ./src/modules/messaging/components/ChannelDetail/Message/UserMessageItem.js